import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AdminRoute } from '@/components/auth/AdminRoute'
import { useRecoverySets, useRecoverySetDashboard, useUnassignedRequestIDs, useCreateRecoverySet } from '../hooks'
import { RecoverySetCard } from '../components/RecoverySetCard'
import { ActiveIncidentBanner } from '../components/ActiveIncidentBanner'
import { CreateRecoverySetDialog } from '../components/CreateRecoverySetDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Settings, LayoutGrid, ListOrdered, Plus, ClipboardList, PlayCircle, CheckCircle2, AlertCircle } from 'lucide-react'
import type { RecoverySetSummary } from '../api'
import type { UnassignedRequestRow } from '../api'
import { formatDateForLocale } from '@/lib/dateUtils'
import { cn } from '@/lib/utils'
import { buildIconPath } from '@/lib/imagePaths'
import { TranslatableText } from '@/components/ui/TranslatableText'

function DoughnutChart({ percentage, size = 32 }: { percentage: number; size?: number }) {
    const r = (size - 4) / 2
    const circumference = 2 * Math.PI * r
    const pct = Math.min(100, Math.max(0, percentage))
    const strokeDashoffset = circumference * (1 - pct / 100)
    return (
        <div className="relative flex-shrink-0" style={{ width: size, height: size }} title={`${Math.round(pct)}%`}>
            <svg width={size} height={size} className="rotate-[-90deg]">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={3} className="text-muted-foreground/30" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="text-green-600 transition-all duration-300"
                />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-foreground">{Math.round(pct)}%</span>
        </div>
    )
}

function TaskStatsCell({ row }: { row: UnassignedRequestRow }) {
    const { t } = useTranslation()
    const noTasks = row.totalTasks ?? 0
    if (noTasks === 0) {
        return <span className="text-sm text-muted-foreground">{t('recoverRequests.noRunbook')}</span>
    }
    const noStarted = row.startedTasks ?? 0
    const noCompleted = row.completedTasks ?? 0
    const noIssues = row.issueTasks ?? 0
    const pct = row.overallPercent ?? Math.round((noCompleted / noTasks) * 100)
    return (
        <div className="flex items-center gap-2 flex-nowrap">
            <DoughnutChart percentage={pct} size={36} />
            <div className="flex items-center gap-1.5 flex-shrink-0">
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-primary/10">
                    <ClipboardList className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-medium">{noTasks}</span>
                </div>
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/10">
                    <PlayCircle className="h-3.5 w-3.5 text-amber-600" />
                    <span className="text-xs font-medium">{noStarted}</span>
                </div>
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-green-500/10">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-xs font-medium">{noCompleted}</span>
                </div>
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-red-500/10">
                    <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                    <span className="text-xs font-medium">{noIssues}</span>
                </div>
            </div>
        </div>
    )
}

function RecoveryStatBadge({ recoveryStatus }: { recoveryStatus: string | null | undefined }) {
    const isInProgress = recoveryStatus?.toLowerCase() === 'inprogress' || recoveryStatus?.toLowerCase() === 'in progress'
    return (
        <span
            className={cn(
                'inline-flex px-2 py-0.5 rounded text-xs font-medium',
                isInProgress ? 'bg-green-500/20 text-green-800 dark:text-green-200' : 'bg-muted text-muted-foreground'
            )}
        >
            {recoveryStatus ?? '—'}
        </span>
    )
}

export function RecoveryManagementPage() {
    const { t, i18n } = useTranslation()
    const locale = i18n.language || 'en-US'
    const navigate = useNavigate()
    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const { data: sets = [], isLoading: setsLoading } = useRecoverySets()
    const { data: dashboard } = useRecoverySetDashboard()
    const { data: unassignedData, isLoading: unassignedLoading } = useUnassignedRequestIDs({ page: 1, limit: 50 })
    const unassignedList = unassignedData?.list ?? []
    const unassignedTotal = unassignedData?.total ?? 0
    const createSet = useCreateRecoverySet()

    const activeIncident = (sets as RecoverySetSummary[]).find(
        (s) => s.requestIdCount > 0 && (s.inProgress > 0 || s.issue > 0)
    )

    return (
        <AdminRoute>
            <div className="p-6 space-y-8">
                <div className="flex items-center gap-3">
                    <Settings className="h-8 w-8 text-muted-foreground" />
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight">
                            {t('navigation.recoveryManagement', 'Recovery Management')}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {t('recoverRequests.managementDescription', 'Recovery management and configuration.')}
                        </p>
                    </div>
                </div>

                {activeIncident && (
                    <ActiveIncidentBanner
                        incidentName={activeIncident.name || `Set ${activeIncident.id}`}
                        startTime={activeIncident.startTime}
                    />
                )}

                {/* Section 1: Recovery Sets — 2 columns */}
                <section className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <h2 className="text-lg font-medium flex items-center gap-2">
                            <LayoutGrid className="h-5 w-5" />
                            {t('recoverRequests.recoverySets', 'Recovery Sets')}
                        </h2>
                        <Button onClick={() => setCreateDialogOpen(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            {t('recoverRequests.createRecoverySet', 'Create Recovery Set')}
                        </Button>
                    </div>
                    <CreateRecoverySetDialog
                        open={createDialogOpen}
                        onOpenChange={setCreateDialogOpen}
                        onSubmit={(payload) =>
                            createSet.mutate(payload, {
                                onSuccess: () => setCreateDialogOpen(false),
                            })
                        }
                        isSubmitting={createSet.isPending}
                    />
                    {setsLoading ? (
                        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                            {t('common.loading', 'Loading…')}
                        </div>
                    ) : sets.length === 0 ? (
                        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                            {t('recoverRequests.noRecoverySets', 'No recovery sets.')}
                        </div>
                    ) : (
                        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                            {sets.map((set) => (
                                <RecoverySetCard
                                    key={set.id}
                                    set={set}
                                    onClick={() => navigate(`/recovery-management/sets/${set.id}`)}
                                    isActiveIncident={activeIncident?.id === set.id}
                                    dashboardSummary={dashboard?.summary ?? null}
                                    dashboardStats={dashboard?.statisticsBySetId?.[set.id] ?? null}
                                />
                            ))}
                        </div>
                    )}
                </section>

                {/* Section 2: Unassigned Request IDs */}
                <section className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-medium flex items-center gap-2">
                            <ListOrdered className="h-5 w-5" />
                            {t('recoverRequests.unassignedRequestIds', 'Unassigned Request IDs')}
                        </h2>
                        {unassignedTotal > 0 && (
                            <Badge variant="secondary" className="font-mono">
                                {unassignedTotal} {t('recoverRequests.requests', 'requests')}
                            </Badge>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {t('recoverRequests.unassignedDescription', 'Request IDs not assigned to any recovery set — click to view progress.')}
                    </p>
                    {unassignedLoading ? (
                        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                            {t('common.loading', 'Loading…')}
                        </div>
                    ) : unassignedList.length === 0 ? (
                        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                            {t('recoverRequests.noUnassignedRequests', 'No unassigned request IDs.')}
                        </div>
                    ) : (
                        <div className="rounded-lg border bg-card overflow-x-auto">
                            <table className="w-full text-sm min-w-[900px]">
                                <thead>
                                    <tr className="border-b bg-muted/50">
                                        <th className="text-left p-3 font-medium">{t('recoverRequests.scenarioName', 'Scenario name')}</th>
                                        <th className="text-left p-3 font-medium">{t('recoverRequests.objectID', 'Object ID')}</th>
                                        <th className="p-1 font-medium w-10"></th>
                                        <th className="text-center px-2 py-3 font-medium">{t('recoverRequests.noObjects', 'No Objects')}</th>
                                        <th className="text-center px-2 py-3 font-medium">{t('recoverRequests.noComplete', 'No Complete')}</th>
                                        <th className="text-center px-2 py-3 font-medium">{t('recoverRequests.noIssues', 'No Issues')}</th>
                                        <th className="text-center px-2 py-3 font-medium">{t('recoverRequests.noGo', 'No Go')}</th>
                                        <th className="text-left p-3 font-medium">{t('recoverRequests.forecastStartTime', 'Forecast start')}</th>
                                        <th className="text-left p-3 font-medium">{t('recoverRequests.forecastFinishTime', 'Forecast finish')}</th>
                                        <th className="text-left p-3 font-medium">{t('recoverRequests.tasksProgress', 'Tasks / Progress')}</th>
                                        <th className="text-left p-3 font-medium">{t('recoverRequests.overallPercent', 'Overall %')}</th>
                                        <th className="text-left p-3 font-medium">{t('recoverRequests.recoveryStatus', 'Recovery status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unassignedList.map((row) => (
                                        <tr
                                            key={`${row.requestID}-${row.objectID ?? row.id}`}
                                            className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                                            onClick={() => navigate(`/recovery-management/requests/${encodeURIComponent(row.requestID)}`)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => e.key === 'Enter' && navigate(`/recovery-management/requests/${encodeURIComponent(row.requestID)}`)}
                                        >
                                            <td className="p-3 text-muted-foreground">{row.scenarioName ? <TranslatableText text={row.scenarioName} /> : '—'}</td>
                                            <td className="p-3">
                                                <div className="flex flex-col min-w-0" title={[row.objectID, row.objectDescription].filter(Boolean).join(' — ') || undefined}>
                                                    <span className="text-sm font-medium truncate">{row.objectID ?? '—'}</span>
                                                    {row.objectDescription != null && row.objectDescription !== '' && (
                                                        <span className="text-xs text-muted-foreground truncate"><TranslatableText text={row.objectDescription} /></span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-1">
                                                {row.iconFilename && (
                                                    <div className="flex items-center justify-center">
                                                        <img
                                                            src={buildIconPath(row.iconFilename)}
                                                            alt=""
                                                            className="h-8 w-8 object-contain"
                                                        />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-2 py-3 tabular-nums text-center">{row.noObjects ?? '—'}</td>
                                            <td className="px-2 py-3 tabular-nums text-center">{row.noComplete ?? '—'}</td>
                                            <td className="px-2 py-3 tabular-nums text-center">{row.issueTasks ?? '—'}</td>
                                            <td className="px-2 py-3 tabular-nums text-center">{row.noGo ?? '—'}</td>
                                            <td className="p-3 text-muted-foreground text-xs">
                                                {row.forecastStartTime
                                                    ? formatDateForLocale(row.forecastStartTime, locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                    : '—'}
                                            </td>
                                            <td className="p-3 text-muted-foreground text-xs">
                                                {row.forecastFinishTime
                                                    ? formatDateForLocale(row.forecastFinishTime, locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                    : '—'}
                                            </td>
                                            <td className="p-3">
                                                <TaskStatsCell row={row} />
                                            </td>
                                            <td className="p-3 tabular-nums text-muted-foreground">
                                                {row.overallPercent != null ? `${Math.round(row.overallPercent)}%` : '—'}
                                            </td>
                                            <td className="p-3">
                                                <RecoveryStatBadge recoveryStatus={row.recoveryStatus} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </AdminRoute>
    )
}
